import React from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { CollegeVisitCompanionLink } from '@backrs_ui/grow-ui/src/pages/CollegeVisitGuide/content';
import { COLLEGE_VISIT_COMPANION_TAB_IDS } from '@backrs_ui/grow-ui/src/pages/CollegeVisitGuide/pages/CollegeVisitCompanionPanel';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { openCustomTab } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';

import { Box, ButtonBase, SxProps, Theme, Typography } from '@mui/material';

type CompanionLinkCardsProps = Readonly<{
  idPrefix: string;
  links: readonly CollegeVisitCompanionLink[];
}>;

/** Cards that open a companion page as a shell tab. */
export const CompanionLinkCards: React.FC<CompanionLinkCardsProps> = ({ idPrefix, links }) => {
  const dispatch = useAppDispatch();

  return (
    <Box sx={stackSx}>
      {links.map((link) => (
        <ButtonBase
          key={link.id}
          id={`${idPrefix}-${link.id}`}
          onClick={() => dispatch(openCustomTab(COLLEGE_VISIT_COMPANION_TAB_IDS[link.companion]))}
          sx={cardSx}
        >
          <Box sx={textSx}>
            <Typography variant="text-md-semibold" sx={titleSx}>
              {link.title}
            </Typography>
            <Typography variant="text-sm-regular" sx={descriptionSx}>
              {link.description}
            </Typography>
          </Box>
          <Box sx={iconSx}>
            <AppIcon
              iconName="ArrowUpRight"
              diameter="18px"
              color="Colors/Foreground/fg-brand-primary"
            />
          </Box>
        </ButtonBase>
      ))}
    </Box>
  );
};

const stackSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  textAlign: 'left',
  width: '100%',
  gap: backrs.spacing['spacing-md'],
  p: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-primary-hover'],
  },
});

const textSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  flex: 1,
  minWidth: 0,
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const iconSx: SxProps<Theme> = () => ({
  display: 'flex',
  flexShrink: 0,
});
